import { useState, useEffect } from 'react';
import { useAtom } from 'jotai';
import { X, Save } from 'lucide-react';
import { currentProjectAtom } from '@/stores/editorStore';
import { apiRequest } from '@/lib/api';
import { toast } from 'sonner';

interface CodeEditorProps {
  filePath: string | null;
  onClose: () => void;
}

export default function CodeEditor({ filePath, onClose }: CodeEditorProps) {
  const [currentProject] = useAtom(currentProjectAtom);
  const [content, setContent] = useState('');
  const [originalContent, setOriginalContent] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (filePath) {
      loadFile();
    }
  }, [filePath]);

  const loadFile = async () => {
    if (!currentProject || !filePath) return;

    setIsLoading(true);
    try {
      const response = await apiRequest<{ content: string }>('get', `/projects/${currentProject.id}/files/read?path=${encodeURIComponent(filePath)}`);
      setContent(response.content);
      setOriginalContent(response.content);
    } catch (error: any) {
      console.error('Failed to load file:', error);
      toast.error(`Failed to load file: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const saveFile = async () => {
    if (!currentProject || !filePath) return;

    setIsSaving(true);
    try {
      await apiRequest('post', `/projects/${currentProject.id}/files/write`, {
        path: filePath,
        content,
      });
      setOriginalContent(content);
      toast.success('File saved successfully');
    } catch (error: any) {
      console.error('Failed to save file:', error);
      toast.error(`Failed to save file: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const hasUnsavedChanges = content !== originalContent;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-background border rounded-lg shadow-lg w-[90vw] h-[90vh] flex flex-col">
        {/* Header */}
        <div className="border-b px-4 py-3 flex items-center justify-between">
          <div>
            <h2 className="font-semibold">Code Editor</h2>
            {filePath && (
              <p className="text-sm text-muted-foreground">{filePath}</p>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={saveFile}
              disabled={!hasUnsavedChanges || isSaving}
              className="flex items-center gap-2 px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" />
              {isSaving ? 'Saving...' : hasUnsavedChanges ? 'Save' : 'Saved'}
            </button>

            <button
              onClick={onClose}
              className="p-1 hover:bg-muted rounded-md transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Editor Content */}
        <div className="flex-1 overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <p className="text-muted-foreground">Loading file...</p>
            </div>
          ) : !filePath ? (
            <div className="flex items-center justify-center h-full">
              <p className="text-muted-foreground">Select a file to edit</p>
            </div>
          ) : (
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="w-full h-full p-4 font-mono text-sm bg-background border-0 focus:outline-none resize-none"
              spellCheck={false}
            />
          )}
        </div>
      </div>
    </div>
  );
}
